import React, { useState } from 'react'
import {
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core'

import { Container, Page } from 'components'
import {
  Header,
  ReportVersionExistsDialog,
  ReportVersionTable,
} from './components'

import useSnackbar from 'hooks/useSnackbar'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import useStyles from './styles'
import constants from 'constants/index'
import helpers from 'helpers'

const ActivitiesReport = () => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const [loading, setLoading] = useState(false)
  const [refresher, setRefresher] = useState(false)
  const [reportType, setReportType] = useState('full')
  const [pendingParams, setPendingParams] = useState({})
  const [currentReportVersion, setCurrentReportVersion] = useState()
  const [reportVersionExistsOpen, setReportVersionExistsOpen] = useState(false)
  const [filters, setFilters] = useState(
    constants.reports.KANBAN_TASKS_EMPTY_FILTERS,
  )

  const { kanbanTasks: description } = constants.reports.DESCRIPTION

  const { response: boardsResponse } = useFetch(
    service.dponet.kanbanBoards.get,
    {},
    [],
  )
  const defaultBoard =
    boardsResponse?.data?.kanbanBoards?.find((board) => board.isDefault) ||
    boardsResponse?.data?.kanbanBoards?.[0]
  const boardId = defaultBoard?.id

  const { response: columnsResponse } = useFetch(
    service.dponet.kanbanColumns.get,
    { kanbanBoardId: boardId },
    [boardId],
    !!boardId,
  )
  const columns = columnsResponse?.data?.kanbanColumns || []

  const handleRefresh = () => setRefresher((prev) => !prev)

  const setFilter = (field) => (event) =>
    setFilters((prev) => ({ ...prev, [field]: event.target.value }))

  const buildCustomParams = () => {
    const params = {}
    if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom
    if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo
    const ALL = constants.reports.KANBAN_TASKS_FILTER_ALL
    if (filters.kanbanColumnId && filters.kanbanColumnId !== ALL)
      params.kanbanColumnId = filters.kanbanColumnId
    if (filters.priority && filters.priority !== ALL)
      params.priority = filters.priority
    if (filters.activityType && filters.activityType !== ALL)
      params.activityType = filters.activityType
    return params
  }

  const hasAnyFilter = Object.values(filters).some((value) => value !== '')

  const isDisabled = loading || (reportType === 'custom' && !hasAnyFilter)

  const submitGenerate = async (extraParams = {}) => {
    if (currentReportVersion?.id) {
      setPendingParams(extraParams)
      setReportVersionExistsOpen(true)
      return
    }
    await dispatchGenerate(extraParams)
  }

  const dispatchGenerate = async (extraParams = {}) => {
    setLoading(true)
    try {
      await service.dponet.kanbanTasks.generateReport({
        reportType,
        ...extraParams,
      })
      snackbar.open({
        message:
          'Relatório sendo gerado. Você receberá um e-mail quando estiver pronto.',
        variant: 'success',
      })
      setTimeout(handleRefresh, 3000)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao solicitar o relatório.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleGenerate = () => {
    if (reportType === 'custom') {
      submitGenerate(buildCustomParams())
    } else {
      submitGenerate()
    }
  }

  const handleVersionExistsConfirm = () => {
    setReportVersionExistsOpen(false)
    dispatchGenerate(pendingParams)
  }

  return (
    <Page className={classes.root} title="Relatório de Atividades">
      <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
        <Box py={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Header
                title={description?.title}
                description={description?.content}
                isLoading={loading}
                buttonAction={handleGenerate}
                disabled={isDisabled}
                buttonType="button"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.label}>
                Escolha a versão do relatório:
              </InputLabel>
              <RadioGroup
                value={reportType}
                onChange={(event) => {
                  setReportType(event.target.value)
                  setFilters(constants.reports.KANBAN_TASKS_EMPTY_FILTERS)
                }}
              >
                {constants.reports.KANBAN_TASKS_REPORT_TYPES.map((type) => (
                  <FormControlLabel
                    key={type.value}
                    value={type.value}
                    control={<Radio classes={{ checked: classes.checked }} />}
                    className={classes.radioLabel}
                    label={type.label}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {reportType === 'custom' && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Prazo — De"
                      type="date"
                      value={filters.dueDateFrom}
                      onChange={setFilter('dueDateFrom')}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Prazo — Até"
                      type="date"
                      value={filters.dueDateTo}
                      onChange={setFilter('dueDateTo')}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Status"
                      value={filters.kanbanColumnId}
                      onChange={setFilter('kanbanColumnId')}
                      fullWidth
                    >
                      <MenuItem
                        value={constants.reports.KANBAN_TASKS_FILTER_ALL}
                      >
                        Todas
                      </MenuItem>
                      {columns.map((column) => (
                        <MenuItem key={column.id} value={String(column.id)}>
                          <Tooltip title={column.title} placement="right">
                            <span>
                              {helpers.truncateUIText(column.title, 30)}
                            </span>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Prioridade"
                      value={filters.priority}
                      onChange={setFilter('priority')}
                      fullWidth
                    >
                      <MenuItem
                        value={constants.reports.KANBAN_TASKS_FILTER_ALL}
                      >
                        Todas
                      </MenuItem>
                      {constants.reports.KANBAN_TASKS_PRIORITIES.map(
                        (priorityOption) => (
                          <MenuItem
                            key={priorityOption.value}
                            value={priorityOption.value}
                          >
                            {priorityOption.label}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Tipo de Atividade"
                      value={filters.activityType}
                      onChange={setFilter('activityType')}
                      fullWidth
                    >
                      <MenuItem
                        value={constants.reports.KANBAN_TASKS_FILTER_ALL}
                      >
                        Todos
                      </MenuItem>
                      {constants.reports.KANBAN_TASKS_ACTIVITY_TYPES.map(
                        (typeOption) => (
                          <MenuItem
                            key={typeOption.value}
                            value={typeOption.value}
                          >
                            {typeOption.label}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <ReportVersionTable
                currentReportVersion={currentReportVersion}
                kind={constants.reportVersions.KIND.KANBAN_TASKS}
                refresher={refresher}
                setCurrentReportVersion={setCurrentReportVersion}
              />
            </Grid>
          </Grid>
        </Box>

        <ReportVersionExistsDialog
          currentReportVersion={currentReportVersion}
          exportAction={handleVersionExistsConfirm}
          open={reportVersionExistsOpen}
          setCurrentReportVersion={setCurrentReportVersion}
          setOpen={setReportVersionExistsOpen}
        />
      </Container>
    </Page>
  )
}

export default ActivitiesReport
